"use client";

import React, { useEffect, useRef, useState } from "react";
import { Instagram, ExternalLink } from "lucide-react";

// Instagram post embed component
const InstagramEmbed = ({ postUrl, height = 600 }) => {
  const containerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Load Instagram embed script
    if (window.instgrm) {
      window.instgrm.Embeds.process();
      setIsLoaded(true);
    } else {
      const script = document.createElement('script');
      script.src = '//www.instagram.com/embed.js';
      script.async = true;
      script.onload = () => {
        if (window.instgrm) {
          window.instgrm.Embeds.process();
          setIsLoaded(true);
        }
      };
      document.body.appendChild(script);
    }
  }, []);

  return (
    <div 
      ref={containerRef}
      className="instagram-embed-container overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
      style={{ height: `${height}px` }}
    >
      {!isLoaded && (
        <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
          <div className="text-center">
            <Instagram className="w-12 h-12 text-purple-600 mx-auto mb-3 animate-pulse" />
            <p className="text-sm text-gray-600">Loading Instagram post...</p>
          </div>
        </div>
      )}
      <blockquote 
        className="instagram-media" 
        data-instgrm-permalink={postUrl}
        data-instgrm-version="14"
        style={{ 
          background: '#FFF',
          border: 0,
          borderRadius: '8px',
          boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
          margin: '1px',
          maxWidth: '540px',
          minWidth: '326px',
          padding: 0,
          width: '99.375%',
          height: '100%'
        }}
      />
    </div>
  );
};

// Masonry grid component for Instagram posts
const InstagramMasonry = ({ posts = [] }) => {
  const [columns, setColumns] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setColumns(1);
      } else if (window.innerWidth < 1024) {
        setColumns(2);
      } else {
        setColumns(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Distribute posts into columns
  const distributeItems = () => {
    const cols = Array.from({ length: columns }, () => []);
    const colHeights = Array(columns).fill(0);

    posts.forEach((post) => {
      // Find column with minimum height
      const minHeight = Math.min(...colHeights);
      const colIndex = colHeights.indexOf(minHeight);
      
      cols[colIndex].push(post);
      colHeights[colIndex] += post.height || 600;
    });

    return cols;
  };

  const columnItems = distributeItems();

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      {/* Header */}
      <div className="text-center mb-8 sm:mb-12">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
          Follow Us on Instagram
        </h2>
        <p className="text-base sm:text-lg text-gray-600 mb-6">
          See our latest jewelry pieces and behind-the-scenes content
        </p>
        <a
          href="https://www.instagram.com/isilverandgem/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          <Instagram className="w-5 h-5" />
          <span className="font-medium">@isilverandgem</span>
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Masonry Grid */}
      <div 
        className="grid gap-4 sm:gap-6"
        style={{
          gridTemplateColumns: `repeat(${columns}, 1fr)`
        }}
      >
        {columnItems.map((column, colIndex) => (
          <div key={colIndex} className="flex flex-col gap-4 sm:gap-6">
            {column.map((post, postIndex) => (
              <div
                key={post.id}
                className="animate-fadeIn"
                style={{
                  animationDelay: `${(colIndex * 0.1) + (postIndex * 0.05)}s`
                }}
              >
                <InstagramEmbed 
                  postUrl={post.url} 
                  height={post.height}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Load More Button */}
      <div className="text-center mt-12">
        <a
          href="https://www.instagram.com/isilverandgem/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 border-2 border-gray-300 rounded-full hover:border-purple-600 hover:text-purple-600 transition-all duration-300 shadow-md hover:shadow-lg font-medium"
        >
          View More on Instagram
          <ExternalLink className="w-5 h-5" />
        </a>
      </div>

      <style jsx global>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
          opacity: 0;
        }

        /* Override Instagram embed styles for better integration */
        .instagram-media {
          margin: 0 !important;
        }
      `}</style>
    </div>
  );
};

// Example usage component
const InstagramFeedSection = () => {
  // Your Instagram posts - replace these with actual Instagram post URLs
  const instagramPosts = [
    {
      id: "1",
      url: "https://www.instagram.com/p/DK9iI14vhXx/",
      height: 600,
    },
    {
      id: "2",
      url: "https://www.instagram.com/p/DK9iI14vhXx/",
      height: 500,
    },
    {
      id: "3",
      url: "https://www.instagram.com/p/DK9iI14vhXx/",
      height: 700,
    },
    {
      id: "4",
      url: "https://www.instagram.com/p/DK9iI14vhXx/",
      height: 550,
    },
    {
      id: "5",
      url: "https://www.instagram.com/p/DK9iI14vhXx/",
      height: 650,
    },
    {
      id: "6",
      url: "https://www.instagram.com/p/DK9iI14vhXx/",
      height: 600,
    },
    {
      id: "7",
      url: "https://www.instagram.com/p/DK9iI14vhXx/",
      height: 500,
    },
    {
      id: "8",
      url: "https://www.instagram.com/p/DK9iI14vhXx/",
      height: 700,
    },
    {
      id: "9",
      url: "https://www.instagram.com/p/DK9iI14vhXx/",
      height: 550,
    },
  ];

  return (
    <section className="bg-gradient-to-b from-white to-gray-50 py-12 sm:py-16 lg:py-20">
      <InstagramMasonry posts={instagramPosts} />
    </section>
  );
};

export default InstagramFeedSection;